import { useTranslate } from '@refinedev/core';
import { Button, Checkbox, Input, Select, Space, Tag, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { SectionDrawer } from '../../components/ToolSection';
import {
  COLOUR_CHIPS,
  HEALTHY_ADULT_ID,
  MAX_OWN_MODES,
  MODE_GROUPS,
  OWN_MODE_KINDS,
  STANDARD_MODES,
  addOwnMode,
  applyPreset,
  canAddOwn,
  draftFromSet,
  draftProblem,
  setInputOf,
  takenOwnIds,
  type ModeMapEntry,
  type SetDraft,
} from './model';
import { useModesCommand } from './useModesCommand';

type Tx = (key: string, options?: Record<string, unknown>) => string;

/**
 * E47 · "Set up the modes with the client" — the very command the client uses (subject = the client), starting from their CURRENT set, as a
 * wide side drawer on the Mode map card (design PORTAL-SECTIONS-2026-10-22 §2.2 (d)1). Each mode is three short lines (checkbox + name,
 * the description, the client's words + the colour chips, which wrap as one block) so a row never has a colour dot alone on a line.
 * `knownMap` is every mode the page has seen — live or switched off — so a new mode of their own never takes a switched-off mode's id.
 */
export const ModesSetupDrawer = ({
  open,
  clientId,
  clientName,
  setMap,
  knownMap,
  lastChangeText,
  onClose,
  onSaved,
}: {
  open: boolean;
  clientId: string;
  clientName: string;
  /** The client's CURRENT set (the draft starts here). */
  setMap: readonly ModeMapEntry[] | undefined;
  /** Every mode seen on the page (the current set + the timeline's map, switched-off ones included). */
  knownMap: readonly ModeMapEntry[];
  lastChangeText: string;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const t = useTranslate() as unknown as Tx;
  const { configureModeSet, isLoading } = useModesCommand();
  const [draft, setDraft] = useState<SetDraft | null>(null);
  const [initial, setInitial] = useState<SetDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  // the draft starts ONCE per opening, from the current set — a refetch while it is open must not wipe what was typed
  const started = useRef(false);
  useEffect(() => {
    if (!open) {
      started.current = false;
      return;
    }
    if (!setMap || started.current) return;
    started.current = true;
    const fresh = draftFromSet(setMap);
    setDraft(fresh);
    setInitial(fresh);
    setError(null);
  }, [open, setMap]);

  const taken = takenOwnIds([...(setMap ?? []), ...knownMap]);
  const edit = (fn: (d: SetDraft) => SetDraft) => {
    if (!draft) return;
    setError(null);
    setDraft(fn(draft));
  };

  const onSave = async () => {
    if (!draft) return;
    if (draftProblem(draft)) {
      setError(t('modes.hub.setup.needsName'));
      return;
    }
    const res = await configureModeSet(
      setInputOf(clientId, draft, (id) => t(`modes.catalog.${id}.name`)),
      t('modes.hub.setup.error'),
    );
    if (res.ok) onSaved();
    else setError(res.message);
  };

  const chips = (
    current: string,
    onPick: (hex: string) => void,
    testPrefix: string,
    label: string,
  ) => (
    <Space size={4} wrap data-testid={testPrefix}>
      {(COLOUR_CHIPS.includes(current)
        ? COLOUR_CHIPS
        : [current, ...COLOUR_CHIPS]
      ).map((hex) => (
        <button
          aria-label={`${label} ${hex}`}
          aria-pressed={hex === current}
          data-testid={`${testPrefix}-${hex}`}
          key={hex}
          onClick={() => onPick(hex)}
          style={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: hex,
            cursor: 'pointer',
            border: hex === current ? '2px solid #1f1f1f' : '1px solid #d9d9d9',
            padding: 0,
          }}
          type="button"
        />
      ))}
    </Space>
  );

  const row: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
  };
  const inputLine: React.CSSProperties = {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
  };

  return (
    <SectionDrawer
      open={open}
      title={t('modes.hub.setup.title', { name: clientName })}
      testId="modes-setup"
      width="wide"
      context={
        <Space direction="vertical" size={4}>
          <Typography.Text type="secondary">
            {t('modes.hub.setup.hint', { name: clientName })}
          </Typography.Text>
          <Typography.Text type="secondary">{lastChangeText}</Typography.Text>
        </Space>
      }
      saveLabel={t('modes.hub.setup.save', { name: clientName })}
      saving={isLoading}
      error={error}
      dirty={JSON.stringify(draft) !== JSON.stringify(initial)}
      onSave={() => {
        onSave().catch(() => undefined);
      }}
      onClose={onClose}
    >
      {!draft ? (
        <Typography.Text type="secondary">
          {t('modes.hub.loading')}
        </Typography.Text>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <Button
              data-testid="modes-setup-preset-commonSeven"
              onClick={() => edit((d) => applyPreset(d, 'commonSeven'))}
            >
              {t('modes.catalog.presets.commonSeven')}
            </Button>
            <Button
              data-testid="modes-setup-preset-allFourteen"
              onClick={() => edit((d) => applyPreset(d, 'allFourteen'))}
            >
              {t('modes.catalog.presets.allFourteen')}
            </Button>
          </Space>
          {MODE_GROUPS.map((group) => (
            <div key={group} data-testid={`modes-setup-group-${group}`}>
              <Typography.Text strong>
                {t(`modes.catalog.groups.${group}`)}
              </Typography.Text>
              {STANDARD_MODES.filter((s) => s.group === group).map((s) => {
                const r = draft.standard[s.modeId];
                const alwaysOn = s.modeId === HEALTHY_ADULT_ID;
                const defaultName = t(`modes.catalog.${s.modeId}.name`);
                return (
                  <div key={s.modeId} style={row}>
                    <div style={inputLine}>
                      <Checkbox
                        aria-label={t('modes.hub.setup.enable', {
                          mode: defaultName,
                        })}
                        checked={r.on}
                        data-testid={`modes-setup-check-${s.modeId}`}
                        disabled={alwaysOn}
                        onChange={(e) =>
                          edit((d) => ({
                            ...d,
                            standard: {
                              ...d.standard,
                              [s.modeId]: {
                                ...d.standard[s.modeId],
                                on: e.target.checked,
                              },
                            },
                          }))
                        }
                      >
                        <strong>{defaultName}</strong>
                      </Checkbox>
                      {alwaysOn ? (
                        <Tag>{t('modes.hub.setup.alwaysOn')}</Tag>
                      ) : null}
                    </div>
                    <Typography.Text type="secondary">
                      {t(`modes.catalog.${s.modeId}.description`)}
                    </Typography.Text>
                    <div style={inputLine}>
                      <Input
                        aria-label={t('modes.catalog.inMyWords')}
                        data-testid={`modes-setup-words-${s.modeId}`}
                        disabled={!r.on}
                        maxLength={60}
                        onChange={(e) =>
                          edit((d) => ({
                            ...d,
                            standard: {
                              ...d.standard,
                              [s.modeId]: {
                                ...d.standard[s.modeId],
                                words: e.target.value,
                              },
                            },
                          }))
                        }
                        placeholder={t('modes.hub.setup.name')}
                        style={{ width: '100%', maxWidth: 320 }}
                        value={r.words}
                      />
                      {chips(
                        r.colour,
                        (hex) =>
                          edit((d) => ({
                            ...d,
                            standard: {
                              ...d.standard,
                              [s.modeId]: {
                                ...d.standard[s.modeId],
                                colour: hex,
                              },
                            },
                          })),
                        `modes-setup-colour-${s.modeId}`,
                        t('modes.hub.setup.colour', { mode: defaultName }),
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          <div data-testid="modes-setup-own">
            <Typography.Text strong>
              {t('modes.hub.setup.ownTitle')}
            </Typography.Text>
            {draft.own.map((o) => (
              <div
                key={o.modeId}
                data-testid={`modes-setup-own-${o.modeId}`}
                style={row}
              >
                <div style={inputLine}>
                  <Input
                    aria-label={t('modes.hub.setup.ownName')}
                    data-testid={`modes-setup-own-${o.modeId}-name`}
                    maxLength={60}
                    onChange={(e) =>
                      edit((d) => ({
                        ...d,
                        own: d.own.map((x) =>
                          x.modeId === o.modeId
                            ? { ...x, words: e.target.value }
                            : x,
                        ),
                      }))
                    }
                    placeholder={t('modes.hub.setup.ownName')}
                    style={{ width: '100%', maxWidth: 320 }}
                    value={o.words}
                  />
                  <Select
                    aria-label={t('modes.hub.setup.ownKind')}
                    data-testid={`modes-setup-own-${o.modeId}-kind`}
                    onChange={(kind) =>
                      edit((d) => ({
                        ...d,
                        own: d.own.map((x) =>
                          x.modeId === o.modeId ? { ...x, kind } : x,
                        ),
                      }))
                    }
                    options={OWN_MODE_KINDS.map((k) => ({
                      value: k,
                      label: t(`modes.catalog.kinds.${k}`),
                    }))}
                    style={{ width: 130 }}
                    value={o.kind}
                  />
                </div>
                <div style={inputLine}>
                  {chips(
                    o.colour,
                    (hex) =>
                      edit((d) => ({
                        ...d,
                        own: d.own.map((x) =>
                          x.modeId === o.modeId ? { ...x, colour: hex } : x,
                        ),
                      })),
                    `modes-setup-own-${o.modeId}-colour`,
                    t('modes.hub.setup.colour', {
                      mode: o.words || t('modes.hub.setup.ownName'),
                    }),
                  )}
                  <Button
                    data-testid={`modes-setup-own-${o.modeId}-remove`}
                    onClick={() =>
                      edit((d) => ({
                        ...d,
                        own: d.own.filter((x) => x.modeId !== o.modeId),
                      }))
                    }
                    size="small"
                  >
                    {t('modes.hub.setup.remove')}
                  </Button>
                </div>
              </div>
            ))}
            <div style={{ paddingTop: 8 }}>
              <Button
                data-testid="modes-setup-add-own"
                disabled={!canAddOwn(draft)}
                onClick={() => edit((d) => addOwnMode(d, taken))}
              >
                + {t('modes.catalog.addOwn')}
              </Button>
              <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                {t('modes.hub.setup.ownLimit', { max: MAX_OWN_MODES })}
              </Typography.Text>
            </div>
          </div>
        </Space>
      )}
    </SectionDrawer>
  );
};
